import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Drawer, Button } from "rsuite";
import { toast } from "react-toastify";
import PromoTitle from "../../../create-promo/PromoTitle";
import Disclaimer from "../../../create-promo/Disclaimer";
import {
  BookingWindowSection,
  StayDatesSection,
  BlackoutDatesSection,
  LeadTimeSection,
  LengthOfStaySection,
} from "../../../create-promo/TargetingConditions/index";
import {
  DiscountTypeSelector,
  DaysAmountInput,
  ApplyToSection,
  RoomsRateplansDrawer,
} from "../../../create-promo/OfferType/index";
import { useTargetingConditionsState } from "../../../create-promo/hooks/useTargetingConditionsState";
import { useOfferTypeState } from "../../../create-promo/hooks/useOfferTypeState";
import { tagCombosWithProperty } from "../../../create-promo/utils/roomRateCombos";
import {
  offerTypes,
  discountTypes,
} from "../../../create-promo/OfferType/offerTypeConstants";
import { desclaimerMessage } from "../../../../utils/utils";
import { deepClone } from "../../../../constants/utils";
import {
  PROMO_OBJECT,
  DEFAULT_CALCULATION_TYPE,
  DISCOUNT_KEY,
  DISCOUNT_TYPE,
} from "../../../../pages/promotions/constants";
import {
  postCampaignAction,
  getRatePlansByProperty,
  getRoomsByProperty,
  getDerivedRateplanApplicableRates,
} from "../../../../api";
import { getApiErrorMessage } from "../../../../utils/apiErrorMessage";
import plusIcon from "../../../../assets/img/plus.svg";
import CampaignStackInfo from "./CampaignStackInfo";
import {
  extractStackingCombinations,
  buildStackingBannerList,
  buildApplicableCombos,
  buildApplicableRatesPayload,
  buildCampaignPromoPayload,
} from "./campaignStacking";
import { CANCEL_LABEL, LAUNCH_PROMO_LABEL } from "../campaignConstants";
import "../../../create-promo/create-promo.scss";
import "../../../../pages/promotions/promotions.scss";
import "./CampaignActionForm.scss";

export const parsePromoObject = (raw) => {
  if (!raw) return null;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const toNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const setByPath = (obj, path, value) => {
  const keys = path.split(".");
  const next = { ...obj };
  let cur = next;
  for (let i = 0; i < keys.length - 1; i++) {
    cur[keys[i]] = Array.isArray(cur[keys[i]])
      ? [...cur[keys[i]]]
      : { ...(cur[keys[i]] || {}) };
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
  return next;
};

const hasValidDateRange = (range) => !!(range?.startDate && range?.endDate);

export const buildInitialTc = (campaign) => {
  const r = campaign?.restrictionConfig || {};
  return {
    showBooking: (r.bookDates || []).some(hasValidDateRange),
    showPSD: (r.stayDates || []).some(hasValidDateRange),
    showBlackouts: (r.blackOutDates || []).some(hasValidDateRange),
    showLOS: !!(r.minLos || r.maxLos),
    showLT: !!(r.minLeadTime || r.maxLeadTime),
  };
};

const getPrimaryPriceConfig = (campaign) => campaign?.priceConfig?.[0] || {};

export const buildDerivedRateRestrictionConfig = (promoState) => {
  const ruleVO = promoState?.rateRule?.ruleVO || {};
  const bookingVO = ruleVO.bookingVO || {};
  const stayVO = ruleVO.stayVO || {};

  const restriction = {};

  if (bookingVO.bookingStartDate && bookingVO.bookingEndDate) {
    restriction.bookDates = [
      {
        startDate: bookingVO.bookingStartDate,
        endDate: bookingVO.bookingEndDate,
      },
    ];
  }

  if (stayVO.stayStartDate && stayVO.stayEndDate) {
    restriction.stayDates = [
      { startDate: stayVO.stayStartDate, endDate: stayVO.stayEndDate },
    ];
  }

  const blackOutDates = (stayVO.blackOutDateRanges || [])
    .filter((b) => b?.blackOutStartDate && b?.blackOutEndDate)
    .map((b) => ({
      startDate: b.blackOutStartDate,
      endDate: b.blackOutEndDate,
      allowStayThrough: b.blackOutStayThruEnabled ?? true,
    }));
  if (blackOutDates.length > 0) restriction.blackOutDates = blackOutDates;

  if (stayVO.minLengthOfStay != null)
    restriction.minLos = stayVO.minLengthOfStay;
  if (stayVO.maxLengthOfStay != null)
    restriction.maxLos = stayVO.maxLengthOfStay;
  if (bookingVO.minBookingLeadTime != null) {
    restriction.minLeadTime = bookingVO.minBookingLeadTime;
  }
  if (bookingVO.maxBookingLeadTime != null) {
    restriction.maxLeadTime = bookingVO.maxBookingLeadTime;
  }

  return restriction;
};

export const buildDerivedRatePriceConfig = (promoState) => {
  const dvo = promoState?.rateRule?.ruleVO?.discountVO || {};
  const isPercent = dvo.mCompDiscountType === offerTypes[0].value;
  const amount = isPercent
    ? dvo.percentDiscountAmount
    : dvo.fixedDiscountAmount;
  const value = String(amount ?? "0");
  const priceDelta = {
    1: value,
    2: value,
    3: value,
    4: value,
    5: value,
    6: value,
    7: value,
  };

  return [
    {
      order: 1,
      priceType: isPercent ? "PERCENT" : "FLAT",
      priceCalculation: "TOTAL",
      priceDelta,
      priceOperation: "SUBTRACT",
      discountType: "ALL_NIGHTS",
    },
  ];
};

export const campaignToPromoObject = (campaign) => {
  const promo = deepClone(PROMO_OBJECT);
  if (!campaign) return promo;
  const r = campaign.restrictionConfig || {};
  const p = getPrimaryPriceConfig(campaign);

  promo.rateRule.ruleVO.descriptionVO.name = campaign.name || "";

  const bookRange = (r.bookDates || []).find(hasValidDateRange);
  if (bookRange) {
    promo.rateRule.ruleVO.bookingVO.bookingStartDate = bookRange.startDate;
    promo.rateRule.ruleVO.bookingVO.bookingEndDate = bookRange.endDate;
  }

  const stayRange = (r.stayDates || []).find(hasValidDateRange);
  if (stayRange) {
    promo.rateRule.ruleVO.stayVO.stayStartDate = stayRange.startDate;
    promo.rateRule.ruleVO.stayVO.stayEndDate = stayRange.endDate;
  }

  promo.rateRule.ruleVO.stayVO.minLengthOfStay = toNum(r.minLos);
  promo.rateRule.ruleVO.stayVO.maxLengthOfStay = toNum(r.maxLos);
  promo.rateRule.ruleVO.bookingVO.minBookingLeadTime = toNum(r.minLeadTime);
  promo.rateRule.ruleVO.bookingVO.maxBookingLeadTime = toNum(r.maxLeadTime);

  promo.rateRule.ruleVO.stayVO.blackOutDateRanges = (r.blackOutDates || [])
    .filter(hasValidDateRange)
    .map((b) => ({
      blackOutStartDate: b.startDate,
      blackOutEndDate: b.endDate,
      blackOutStayThruEnabled: b.allowStayThrough ?? true,
    }));

  // Map price config → discountVO
  promo.rateRule.ruleVO.discountVO.priceAdjustment =
    p.priceOperation === "ADDITION" ? "INCREASE" : "DECREASE";
  promo.rateRule.ruleVO.discountVO.mCompDiscountType =
    p.priceType === "FIXED" ? offerTypes[1].value : offerTypes[0].value;

  const deltaValues = p.priceDelta
    ? Object.values(p.priceDelta).map((v) => Number(v) || 0)
    : [];
  // Default to "All days of the week" when priceDelta is missing/empty
  // or when every day shares the same value.
  const allEqual =
    deltaValues.length === 0 || deltaValues.every((v) => v === deltaValues[0]);
  promo.rateRule.ruleVO.discountVO.discountType = allEqual
    ? discountTypes[0].value
    : discountTypes[1].value;

  const dayMapNumeric = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
  if (p.priceDelta) {
    Object.entries(p.priceDelta).forEach(([k, v]) => {
      dayMapNumeric[k] = Number(v) || 0;
    });
  }
  if (p.priceType === "FIXED") {
    promo.rateRule.ruleVO.discountVO.fixedDiscountMap = dayMapNumeric;
    promo.rateRule.ruleVO.discountVO.fixedDiscountAmount = allEqual
      ? deltaValues[0]
      : 0;
  } else {
    promo.rateRule.ruleVO.discountVO.percentDiscountMap = dayMapNumeric;
    promo.rateRule.ruleVO.discountVO.percentDiscountAmount = allEqual
      ? deltaValues[0]
      : 0;
  }

  return promo;
};

const priceCalcToNumber = (priceCalculation) => {
  if (priceCalculation === "TOTAL") return 1;
  if (priceCalculation === "NIGHTLY") return 0;
  return DEFAULT_CALCULATION_TYPE;
};

const CampaignActionForm = ({
  recommendation,
  selectedProperty,
  onClose,
  onSuccess,
}) => {
  const campaign = useMemo(
    () => parsePromoObject(recommendation?.promoObject),
    [recommendation]
  );

  const [promoState, setPromoState] = useState(() =>
    campaignToPromoObject(campaign)
  );
  const [tc, setTc] = useState(() => buildInitialTc(campaign));
  // Frozen at mount: was LOS already on the campaign the reviewer opened?
  // If yes, LOS stays locked (gray, non-editable) like the other pre-set
  // targeting fields. If no, the reviewer can add + edit it inline.
  const initialHadLos = useMemo(
    () =>
      !!(
        campaign?.restrictionConfig?.minLos ||
        campaign?.restrictionConfig?.maxLos
      ),
    [campaign]
  );
  const [submitting, setSubmitting] = useState(null);
  const [calculationType, setCalculationType] = useState(
    priceCalcToNumber(getPrimaryPriceConfig(campaign).priceCalculation)
  );

  const [ratePlans, setRatePlans] = useState([]);
  const [rooms, setRooms] = useState([]);
  // Property the rate plans/rooms above belong to — combos are tagged with it
  // so a selection can never be submitted under a different property.
  const [ratePlansPropertyId, setRatePlansPropertyId] = useState(null);
  const [stackingCombinations, setStackingCombinations] = useState([]);

  const handleFieldChange = useCallback((path, value) => {
    setPromoState((prev) => setByPath(prev, path, value));
  }, []);

  const tcState = useTargetingConditionsState({
    promo: promoState,
    handleFieldChange,
    tc,
    setTc,
    setIsNextBtnDisabled: () => {},
  });

  const offer = useOfferTypeState({
    onFieldChange: handleFieldChange,
    promo: promoState,
    setIsNextBtnDisabled: () => {},
    calculationType,
    setCalculationType,
    isEdit: false,
    isDuplicate: false,
    isMarket: false,
    ratePlans,
    rooms,
    ratePlansPropertyId,
    // Campaign approve flow filters out the "All Rooms & Rate Plans" option,
    // so the dropdown value must always resolve to "select" — otherwise the
    // empty case (before combos load) or the all-combos case would leave the
    // picker showing its empty placeholder.
    forceSelectOnly: true,
  });

  useEffect(() => {
    const pid = selectedProperty?.propertyId;
    if (!pid) return undefined;

    // Clear first, and ignore a response for a property that is no longer
    // selected: showing one property's rate plans under another property's id
    // is what lets a promo be built from foreign rateplan ids.
    setRatePlans([]);
    setRooms([]);
    setRatePlansPropertyId(null);

    let cancelled = false;
    Promise.all([getRatePlansByProperty(pid), getRoomsByProperty(pid)])
      .then(([rpRes, rmRes]) => {
        if (cancelled) return;
        setRatePlans(rpRes?.data?.data ?? rpRes?.data ?? []);
        setRooms(rmRes?.data?.data ?? rmRes?.data ?? []);
        setRatePlansPropertyId(pid);
      })
      .catch(() => {
        if (!cancelled) toast.error("Unable to load rate plans / rooms");
      });

    return () => {
      cancelled = true;
    };
  }, [selectedProperty?.propertyId]);

  // Fetch the derived-rateplan applicable rates for this campaign. The
  // response drives the stacking banner + Rooms/RatePlans matrix. Payload
  // pulls clientId from recommendation.ownerId (that field IS the buyer's
  // client id) and propertyId from the resolved property; if any required
  // piece is missing the fetch is skipped and the banner + grid stay empty.
  useEffect(() => {
    const payload = buildApplicableRatesPayload({
      campaign,
      propertyId: selectedProperty?.propertyId ?? recommendation?.propertyId,
      clientId: recommendation?.ownerId,
    });
    if (!payload) {
      // Clear so a previous recommendation's response doesn't bleed through
      // when this modal is reused for a different, un-fetchable one.
      setStackingCombinations([]);
      return undefined;
    }

    let cancelled = false;
    getDerivedRateplanApplicableRates(payload)
      .then((res) => {
        if (cancelled) return;
        setStackingCombinations(extractStackingCombinations(res));
      })
      .catch(() => {
        if (!cancelled) setStackingCombinations([]);
      });
    return () => {
      cancelled = true;
    };
    // `campaign` is memoized on `recommendation`, so any change to
    // recommendation.propertyId / .ownerId already triggers a new campaign
    // reference and re-runs this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign, selectedProperty?.propertyId]);

  // Applicable rooms × rate plans and the stacking-banner list both come off
  // the derived-rateplan applicable-rates endpoint. See ./campaignStacking.js
  // for the mapping rules.
  const applicableCombos = useMemo(
    () =>
      tagCombosWithProperty(
        buildApplicableCombos(stackingCombinations, rooms, ratePlans),
        ratePlansPropertyId
      ),
    [stackingCombinations, rooms, ratePlans, ratePlansPropertyId]
  );

  const stackingBannerDescriptions = useMemo(
    () => buildStackingBannerList(stackingCombinations),
    [stackingCombinations]
  );

  // Drop the "All Rooms & Rate Plans" choice — the campaign approve flow
  // only makes sense with the applicable subset. Only "Selected Rooms &
  // Rate Plans" stays in the dropdown.
  const roomsRateplansOptionsWithoutAll = useMemo(
    () =>
      (offer.roomsRateplansOptions || []).filter(
        (option) => option.value !== "all"
      ),
    [offer.roomsRateplansOptions]
  );

  // Push the applicable subset into promoState so the (shared, untouched)
  // RoomsRateplans grid renders green only for those combos via its own
  // getStatus path, and seed allowedRateRoomCodesByProperty[pid] so the
  // drawer-open handler in useOfferTypeState.applySelections treats it as
  // a saved selection instead of overwriting it with all combos.
  useEffect(() => {
    if (applicableCombos.length === 0) return;
    handleFieldChange("allowedRateRoomCodes", applicableCombos);
    const pid =
      selectedProperty?.propertyId ?? recommendation?.propertyId ?? null;
    if (pid != null) {
      handleFieldChange("allowedRateRoomCodesByProperty", {
        [pid]: applicableCombos,
      });
    }
  }, [
    applicableCombos,
    handleFieldChange,
    selectedProperty?.propertyId,
    recommendation?.propertyId,
  ]);

  // Body-level class scopes the "campaign approve" CSS overrides on the
  // shared Rooms & Rate Plans drawer (hides Select/Deselect All, disables
  // Confirm Selections, blocks pointer events on the grid). Only present
  // while this form is mounted — Create/Edit/Smart flows are untouched.
  useEffect(() => {
    document.body.classList.add("campaign-review-active");
    return () => {
      document.body.classList.remove("campaign-review-active");
    };
  }, []);

  // The row / column header buttons in the shared grid keep native
  // `title` tooltips (so the reviewer can hover a truncated room-type or
  // rateplan name to see the full text). Since we can't touch the shared
  // component, we let those buttons stay hover-active and swallow their
  // clicks here so `selectAllRPForRoom` / `selectAllRoomsForRateplan`
  // don't fire and drift the read-only selection.
  useEffect(() => {
    const handler = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (
        target.closest(
          ".rooms-rateplans-drawer .room-type-cell, .rooms-rateplans-drawer .rateplan-name"
        )
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  if (!campaign) return null;

  const discountVO = promoState.rateRule.ruleVO.discountVO;

  // Campaign rule: the discount can be raised but not dropped below
  // what the buyer originally proposed. Allow free typing; flag the
  // field with an error and block submit when the value goes below.
  const initialPriceDelta = getPrimaryPriceConfig(campaign).priceDelta || {};
  const initialDayMins = Object.fromEntries(
    Object.entries(initialPriceDelta).map(([k, v]) => [
      k,
      Number.parseFloat(v) || 0,
    ])
  );
  const initialAllDaysMin = Math.max(0, ...Object.values(initialDayMins));

  const isDiscountBelowMin = (() => {
    const dvo = promoState?.rateRule?.ruleVO?.discountVO;
    if (!dvo) return false;
    const isPercent = dvo.mCompDiscountType === offerTypes[0].value;
    const isDayWise = dvo.discountType === discountTypes[1].value;

    if (isDayWise) {
      const map = isPercent
        ? dvo.percentDiscountMap || {}
        : dvo.fixedDiscountMap || {};
      return Object.entries(initialDayMins).some(([day, min]) => {
        const current = Number(map[day]) || 0;
        return current < min;
      });
    }
    const amount = isPercent
      ? dvo.percentDiscountAmount
      : dvo.fixedDiscountAmount;
    return (Number(amount) || 0) < initialAllDaysMin;
  })();

  const propertyIdForPayload =
    selectedProperty?.propertyId ?? recommendation.propertyId ?? null;
  const campaignName =
    promoState?.rateRule?.ruleVO?.descriptionVO?.name ||
    recommendation.campaignName;

  const buildPromoPayload = () =>
    buildCampaignPromoPayload({
      stackingCombinations,
      propertyId: propertyIdForPayload,
      campaignId: recommendation.campaignId,
      name: campaignName,
      priceConfig: buildDerivedRatePriceConfig(promoState),
      restrictionConfig: buildDerivedRateRestrictionConfig(promoState),
      clientId:
        recommendation?.ownerId != null ? Number(recommendation.ownerId) : null,
    });

  // The campaign decision and the promo it launches travel in a single request:
  // the promo body with the action fields merged on top. Spread order matters —
  // propertyId is only top-level because it is applied after the promo body,
  // which carries it nested under rateRoomSelections.
  const submitAction = async (action, text = "") => {
    setSubmitting(action);
    try {
      await postCampaignAction({
        ...buildPromoPayload(),
        propertyId: propertyIdForPayload,
        action,
        comment: text,
      });
      toast.success("Promo launched");
      onSuccess?.(action);
    } catch (err) {
      toast.error(`Failed to launch promo: ${getApiErrorMessage(err)}`);
    } finally {
      setSubmitting(null);
    }
  };

  const handleApproveClick = () => {
    if (isApproveDisabled) {
      if (!promoState.rateRule.ruleVO.descriptionVO.name?.trim()) {
        toast.error("Name is required");
      } else if (isDiscountBelowMin) {
        toast.error("Amount can't be lower than the buyer's proposed value");
      } else if (offer.discountLimitError) {
        toast.error(offer.discountLimitError);
      } else if (applicableCombos.length === 0) {
        toast.error("Rooms and rate plans are still loading");
      } else {
        toast.error("Campaign details are incomplete");
      }
      return;
    }
    // Fire-and-forget: submitAction handles its own success/error toasts and
    // resets submitting state in a finally block.
    void submitAction("APPROVE");
  };

  const checkHasDiscount = (discountVO, discountType, offerType) => {
    const keys = DISCOUNT_KEY[offerType];
    if (!keys) return false;
    if (discountType === DISCOUNT_TYPE.DAY_WISE) {
      const map = discountVO?.[keys.map] || {};
      return Object.values(map).some((v) => v && Number.parseFloat(v) > 0);
    }
    return discountVO?.[keys.amount] > 0;
  };

  const isApproveDisabled = (() => {
    const promoName = promoState?.rateRule?.ruleVO?.descriptionVO?.name;
    const dvo = promoState?.rateRule?.ruleVO?.discountVO;
    const offerType = dvo?.mCompDiscountType;
    const discType = dvo?.discountType;

    const hasName = !!promoName?.trim();
    const hasDiscount = checkHasDiscount(dvo, discType, offerType);
    const hasProperties = (offer.selectedProperties || []).length > 0;
    // Rooms & rate plans are read-only in the campaign flow; the grid shows
    // the applicable subset from the API. Require at least one combo in that
    // subset so Launch Promo isn't clickable before rooms/RPs have loaded.
    const hasRoomRateplanSelection = applicableCombos.length > 0;

    return (
      !(
        hasName &&
        offerType &&
        discType &&
        hasDiscount &&
        hasRoomRateplanSelection &&
        hasProperties
      ) ||
      isDiscountBelowMin ||
      // Above the 100% cap. Same treatment as the below-min rule: the typed
      // value stays in the field with the warning under it, and Launch Promo
      // is what blocks it.
      !!offer.discountLimitError
    );
  })();

  const lockedTcExists =
    tc.showBooking || tc.showPSD || tc.showLT || (initialHadLos && tc.showLOS);

  return (
    <Drawer
      placement="right"
      open
      onClose={onClose}
      size="lg"
      className="create-promo-drawer campaign-action-drawer"
    >
      <Drawer.Header>
        <Drawer.Title>Review Campaign Recommendation</Drawer.Title>
      </Drawer.Header>
      <Drawer.Body>
        <div className="create-promo">
          <div className="create-promo-body create-promo-single-view">
            <CampaignStackInfo descriptions={stackingBannerDescriptions} />

            <div className="form-section">
              <div className="create-promo-disabled">
                <PromoTitle
                  setIsNextBtnDisabled={() => {}}
                  fieldValue={
                    promoState?.rateRule?.ruleVO?.descriptionVO?.name || ""
                  }
                  onFieldChange={handleFieldChange}
                />
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section-title tc">Targeting Conditions</h3>
              <div className="targetting-conditions">
                <Disclaimer DisclaimerMessage={desclaimerMessage} />

                {lockedTcExists && (
                  <fieldset className="campaign-locked-fieldset" disabled>
                    {tc.showBooking && (
                      <BookingWindowSection
                        fromBooking={tcState.fromBooking}
                        toBooking={tcState.toBooking}
                        onBookingChange={tcState.handleBookingChange}
                        onRemove={tcState.setShowBooking}
                        hasError={tcState.hasBookingDateError}
                      />
                    )}

                    {tc.showPSD && (
                      <StayDatesSection
                        stayDateRanges={tcState.stayDateRanges}
                        onStayDateRangeChange={
                          tcState.handleStayDateRangeChange
                        }
                        onRemoveStayDateRange={tcState.removeStayDateRange}
                        onAddStayDateRange={tcState.addStayDateRange}
                        onRemoveSection={tcState.setShowPSD}
                        hasErrors={tcState.stayDateRangeErrors}
                      />
                    )}

                    {tc.showLT && (
                      <LeadTimeSection
                        ltType={tcState.ltType}
                        minBookingLeadTime={tcState.minBookingLeadTime}
                        maxBookingLeadTime={tcState.maxBookingLeadTime}
                        onLtTypeChange={tcState.setLTType}
                        onMinMaxChange={tcState.setLTMinMax}
                        onRangeChange={tcState.setLTRange}
                        onRemove={tcState.setShowLT}
                        hasError={tcState.hasLeadTimeError}
                      />
                    )}

                    {initialHadLos && tc.showLOS && (
                      <LengthOfStaySection
                        losType={tcState.losType}
                        minLengthOfStay={tcState.minLos}
                        maxLengthOfStay={tcState.maxLos}
                        onLosTypeChange={tcState.setLOSType}
                        onMinMaxChange={tcState.setLosMinMax}
                        onRangeChange={tcState.setLosRange}
                        onRemove={tcState.setShowLOS}
                        hasError={tcState.hasLosError}
                      />
                    )}
                  </fieldset>
                )}

                {tc.showBlackouts && (
                  <BlackoutDatesSection
                    blackoutDates={tcState.blackoutDates}
                    onBlackoutDateChange={tcState.setBODates}
                    onRemoveBlackoutDate={tcState.removeBlackoutDate}
                    onAddBlackoutDate={tcState.addBlackoutDate}
                    onToggleAllowStayThrough={tcState.handleAllowStayThrough}
                  />
                )}

                {!initialHadLos && tc.showLOS && (
                  <div className="campaign-editable-los">
                    <LengthOfStaySection
                      losType={tcState.losType}
                      minLengthOfStay={tcState.minLos}
                      maxLengthOfStay={tcState.maxLos}
                      onLosTypeChange={tcState.setLOSType}
                      onMinMaxChange={tcState.setLosMinMax}
                      onRangeChange={tcState.setLosRange}
                      onRemove={tcState.setShowLOS}
                      hasError={tcState.hasLosError}
                    />
                  </div>
                )}

                {(!tc.showBlackouts || (!initialHadLos && !tc.showLOS)) && (
                  <div className="condition-buttons-section">
                    {!tc.showBlackouts && (
                      <button
                        type="button"
                        className="condition-button"
                        onClick={tcState.setShowBlackouts}
                      >
                        <img src={plusIcon} alt="Plus" className="plus-icon" />
                        <span>Blackout Dates</span>
                      </button>
                    )}
                    {!initialHadLos && !tc.showLOS && (
                      <button
                        type="button"
                        className="condition-button"
                        onClick={tcState.setShowLOS}
                      >
                        <img src={plusIcon} alt="Plus" className="plus-icon" />
                        <span>Length of Stay</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section-title">Price Adjustment</h3>
              <div className="offer-type">
                <div className="offer-type-group">
                  <div className="create-promo-disabled">
                    <DiscountTypeSelector
                      priceAdjustment={discountVO.priceAdjustment || "DECREASE"}
                      onPriceAdjustmentChange={
                        offer.handlePriceAdjustmentChange
                      }
                      discountType={discountVO.mCompDiscountType}
                      onDiscountTypeChange={offer.handleChange}
                      calculationType={calculationType}
                      onCalculationTypeChange={(v) =>
                        offer.handleCalculationTypeChange(v, 0)
                      }
                      calculationOptions={offer.calculationOptions}
                      adjustmentOptions={offer.adjustmentOptions}
                    />
                  </div>

                  <div
                    className={`campaign-days-amount-wrapper${
                      isDiscountBelowMin
                        ? " campaign-days-amount-wrapper--error"
                        : ""
                    }`}
                  >
                    <DaysAmountInput
                      discountType={discountVO.discountType}
                      onDiscountTypeChange={offer.handleDiscountTypeChange}
                      mCompDiscountType={discountVO.mCompDiscountType}
                      discountValue={offer.getDiscountValue}
                      onDiscountChange={offer.handleDiscountChange}
                      percentDiscountMap={discountVO.percentDiscountMap}
                      fixedDiscountMap={discountVO.fixedDiscountMap}
                      onDayChange={offer.handleDayChange}
                      errorMessage={offer.discountLimitError}
                      onKeyDown={offer.handleKeyDown}
                    />
                    {isDiscountBelowMin && (
                      <p className="campaign-days-amount-error">
                        Amount can&apos;t be lower than the buyer&apos;s
                        proposed value.
                      </p>
                    )}
                  </div>
                </div>

                <ApplyToSection
                  isMarket={false}
                  isEdit
                  selectedProperty={offer.selectedProperty}
                  selectedProperties={offer.selectedProperties}
                  onPropertiesChange={offer.setSelectedProperties}
                  onPropertiesBlur={offer.handlePropertiesBlur}
                  propertyOptions={offer.propertyOptions}
                  roomsRateplansValue={offer.roomsRateplansValue}
                  onRoomsRateplansChange={offer.handleRoomsRateplansValueChange}
                  roomsRateplansOptions={roomsRateplansOptionsWithoutAll}
                  onSelectClick={() => offer.setShowRoomsRateplansModal(true)}
                  buttonText={offer.getButtonText()}
                  buttonClassName={offer.getButtonClassName()}
                  isButtonDisabled={false}
                  onFieldChange={handleFieldChange}
                />

                <RoomsRateplansDrawer
                  open={offer.showRoomsRateplansModal}
                  onClose={offer.handleDrawerClose}
                  selectedProperties={offer.selectedProperties}
                  selectedHotelInDrawer={offer.selectedHotelInDrawer}
                  setSelectedHotelInDrawer={offer.setSelectedHotelInDrawer}
                  filteredPropertyOptions={
                    offer.filteredPropertyOptionsForHotel
                  }
                  properties={offer.properties}
                  selectedProperty={offer.selectedProperty}
                  hotelSelectContainerRef={offer.hotelSelectContainerRef}
                  drawerRatePlans={offer.drawerRatePlans}
                  setDrawerRatePlans={offer.setDrawerRatePlans}
                  drawerRooms={offer.drawerRooms}
                  setDrawerRooms={offer.setDrawerRooms}
                  drawerDataPropertyId={offer.drawerDataPropertyId}
                  setDrawerDataPropertyId={offer.setDrawerDataPropertyId}
                  drawerSelections={offer.drawerSelections}
                  setDrawerSelections={offer.setDrawerSelections}
                  promo={promoState}
                  promoData={offer.promoData}
                  onFieldChange={handleFieldChange}
                  isEdit={false}
                  successMessage={offer.successMessage}
                  setSuccessMessage={offer.setSuccessMessage}
                  getRatePlansByProperty={getRatePlansByProperty}
                  getRoomsByProperty={getRoomsByProperty}
                  buildAllRoomRateplanCombos={offer.buildAllRoomRateplanCombos}
                  savedSelectionsBeforeDrawerRef={
                    offer.savedSelectionsBeforeDrawerRef
                  }
                  roomsRateplansByPropertyRef={
                    offer.roomsRateplansByPropertyRef
                  }
                />
              </div>
            </div>

            <div className="campaign-actions">
              <Button
                onClick={onClose}
                disabled={!!submitting}
                className="cf-btn-cancel"
              >
                {CANCEL_LABEL}
              </Button>
              <Button
                onClick={handleApproveClick}
                disabled={!!submitting || isApproveDisabled}
                className="cf-btn-approve"
                loading={submitting === "APPROVE"}
              >
                {LAUNCH_PROMO_LABEL}
              </Button>
            </div>
          </div>
        </div>
      </Drawer.Body>
    </Drawer>
  );
};

CampaignActionForm.propTypes = {
  recommendation: PropTypes.shape({
    campaignId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      .isRequired,
    propertyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    promoObject: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    campaignName: PropTypes.string,
    ownerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  selectedProperty: PropTypes.shape({
    propertyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    propertyName: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default CampaignActionForm;
